import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { TrendingUp } from "lucide-react";

const RANGES = [
  { label: "7d", days: 7 },
  { label: "14d", days: 14 },
  { label: "30d", days: 30 },
];

const fmtDate = (d) => {
  const [, m, day] = d.split("-");
  return `${m}/${day}`;
};

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0a0a0a] border border-[#2a2a2a] rounded-lg px-3 py-2 text-xs">
      <p className="font-mono text-[#9ca3af] mb-1">{fmtDate(label)}</p>
      {payload.map((p) => (
        <p key={p.dataKey} style={{ color: p.color }} className="font-medium">
          {p.dataKey === "revenue" ? `$${p.value.toFixed(2)}` : `${p.value}`} {p.dataKey === "revenue" ? "revenue" : "plays"}
        </p>
      ))}
    </div>
  );
}

export const AnalyticsChart = () => {
  const [data, setData] = useState([]);
  const [days, setDays] = useState(14);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/stats/timeseries?days=${days}`)
      .then((r) => setData(r.data))
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, [days]);

  const hasData = data.some((d) => d.plays > 0 || d.revenue > 0);

  return (
    <div className="bg-[#121212] border border-[#2a2a2a] rounded-2xl p-6" data-testid="analytics-chart">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading text-xl font-semibold flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-[#e2ff31]" /> Plays & revenue over time
        </h2>
        <div className="flex gap-1 bg-[#0a0a0a] border border-[#2a2a2a] rounded-full p-1">
          {RANGES.map((r) => (
            <button key={r.days} data-testid={`range-${r.label}`} onClick={() => setDays(r.days)}
              className={`text-xs font-medium rounded-full px-3 py-1 transition-colors ${days === r.days ? "bg-[#e2ff31] text-black" : "text-[#9ca3af] hover:text-white"}`}>
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {!hasData && !loading && (
        <p className="text-sm text-[#6b7280] text-center py-16">
          No activity yet. Plays and revenue will appear here as listeners stream your beats and ads run.
        </p>
      )}

      {(hasData || loading) && (
        <ResponsiveContainer width="100%" height={280}>
          <ComposedChart data={data} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
            <defs>
              <linearGradient id="playsFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#e2ff31" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#e2ff31" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e1e1e" vertical={false} />
            <XAxis dataKey="date" tickFormatter={fmtDate} stroke="#6b7280" fontSize={11} tickLine={false} axisLine={false} />
            <YAxis yAxisId="left" stroke="#6b7280" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
            <YAxis yAxisId="right" orientation="right" stroke="#22c55e" fontSize={11} tickLine={false} axisLine={false}
              tickFormatter={(v) => `$${v}`} />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Area yAxisId="left" type="monotone" dataKey="plays" name="Plays" stroke="#e2ff31" strokeWidth={2} fill="url(#playsFill)" />
            <Line yAxisId="right" type="monotone" dataKey="revenue" name="Revenue ($)" stroke="#22c55e" strokeWidth={2} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};
